'use client'

import * as React from 'react'
import { LazyMotion, domAnimation, m, useInView, useReducedMotion } from 'framer-motion'
import { EASE, SPRING } from '@/lib/motion'

// The rights state machine as an animated timeline for /rights-management.
// The main line plays pending → approved, then the lifecycle forks:
// revoked or expired after approval, rejected as the branch off pending.
// State chips pop in sequence and the connecting rails draw in
// (scaleY/scaleX, transform-only) the first time the section scrolls in.
// Plays once; reduced motion renders the final diagram immediately.
// Copy matches the five states documented on the page.

const FORKS = [
  {
    from: 'pending',
    state: 'rejected',
    tone: 'bg-rose-500/10 text-rose-700',
    body: 'Creator declined. Post hidden permanently. Logged for audit + analytics.',
    delay: 0.65,
  },
  {
    from: 'approved',
    state: 'revoked',
    tone: 'bg-rose-500/10 text-rose-700',
    body: 'Creator pulled consent after the fact. Idukki removes the post from every surface within minutes.',
    delay: 1.25,
  },
  {
    from: 'approved',
    state: 'expired',
    tone: 'bg-slate-500/10 text-slate-700',
    body: 'Default 2 years from signature. Triggers a renewal-request flow before the deadline.',
    delay: 1.4,
  },
]

function StateChip({ label, tone, done, reduce, delay }: {
  label: string
  tone: string
  done: boolean
  reduce: boolean
  delay: number
}) {
  return (
    <m.span
      className={`inline-flex w-fit shrink-0 rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider ${tone}`}
      initial={reduce ? false : { opacity: 0, scale: 0.8 }}
      animate={done ? { opacity: 1, scale: 1 } : undefined}
      transition={reduce ? { duration: 0 } : { ...SPRING, delay }}
    >
      {label}
    </m.span>
  )
}

export function RightsStateTimeline() {
  const ref = React.useRef<HTMLDivElement>(null)
  const inView = useInView(ref, { once: true, margin: '0px 0px -10% 0px' })
  const reduce = useReducedMotion() ?? false
  const done = inView
  const d = (n: number) => (reduce ? 0 : n)

  const fade = (delay: number) => ({
    initial: reduce ? (false as const) : { opacity: 0, y: 10 },
    animate: done ? { opacity: 1, y: 0 } : undefined,
    transition: { duration: 0.4, ease: EASE, delay: d(delay) },
  })

  return (
    <LazyMotion features={domAnimation}>
      <div ref={ref} className="mt-8 rounded-2xl border bg-card p-6 md:p-8">
        <div className="grid gap-8 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          {/* Main line: pending → approved */}
          <div>
            <p className="text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">The main line</p>

            <m.div {...fade(0.1)} className="mt-4 flex items-start gap-3">
              <StateChip label="pending" tone="bg-amber-500/10 text-amber-700" done={done} reduce={reduce} delay={d(0.15)} />
              <p className="text-sm text-muted-foreground">Post ingested, no consent yet. Hidden from widgets by default.</p>
            </m.div>

            {/* Rail pending → approved */}
            <div className="ml-7 mt-2 h-8 w-px overflow-hidden">
              <m.span
                className="block h-full w-full bg-border"
                style={{ originY: 0 }}
                initial={reduce ? false : { scaleY: 0 }}
                animate={done ? { scaleY: 1 } : undefined}
                transition={{ duration: 0.3, ease: EASE, delay: d(0.45) }}
              />
            </div>

            <m.div {...fade(0.75)} className="mt-2 flex items-start gap-3">
              <StateChip label="approved" tone="bg-emerald-500/10 text-emerald-700" done={done} reduce={reduce} delay={d(0.85)} />
              <p className="text-sm text-muted-foreground">
                Creator signed. <code className="rounded bg-secondary px-1 py-0.5 font-mono text-[11px]">confirmation_from_user: true</code>.
                Surfaces in widgets and exports.
              </p>
            </m.div>

            <m.p {...fade(1.05)} className="ml-7 mt-4 text-[11px] text-muted-foreground">
              Approval is the only gate. There is no separate &ldquo;published&rdquo; toggle to forget.
            </m.p>
          </div>

          {/* The forks */}
          <div>
            <p className="text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">Where it can fork</p>
            <ul className="mt-4 space-y-3">
              {FORKS.map((f) => (
                <m.li
                  key={f.state}
                  {...fade(f.delay)}
                  className="flex items-start gap-3 rounded-xl border border-dashed bg-secondary/30 p-3.5"
                >
                  <div className="flex shrink-0 flex-col items-start gap-1">
                    <span className="font-mono text-[10px] text-muted-foreground">{f.from} →</span>
                    <StateChip label={f.state} tone={f.tone} done={done} reduce={reduce} delay={d(f.delay + 0.1)} />
                  </div>
                  <p className="text-sm text-muted-foreground">{f.body}</p>
                </m.li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </LazyMotion>
  )
}
